import React, { useState } from 'react';
import {
    createCustomerPortalSession,
    CustomerPortalAction
} from '../../services/payment.service';
import { getPaymentErrorMessage } from '../../utils/payment-error';
import styles from './BillingPortalPanel.module.scss';

interface BillingPortalPanelProps {
    token: string;
    isCancellationScheduled: boolean;
    hasManagedSubscription: boolean;
}

interface BillingAction {
    action: CustomerPortalAction;
    title: string;
    description: string;
    buttonLabel: string;
}

const BillingPortalPanel = ({
    token,
    isCancellationScheduled,
    hasManagedSubscription
}: BillingPortalPanelProps) => {
    const [loadingAction, setLoadingAction] = useState<CustomerPortalAction | null>(null);
    const [portalErrors, setPortalErrors] = useState<Partial<Record<CustomerPortalAction, string>>>({});

    const billingActions: BillingAction[] = [
        {
            action: 'payment_method_update',
            title: 'Update payment method',
            description: 'Replace your card or update the payment method Stripe uses for future charges.',
            buttonLabel: 'Update payment method'
        },
        {
            action: 'invoice_history',
            title: 'Invoices and receipts',
            description: 'View and download your Stripe-hosted billing history.',
            buttonLabel: 'View invoices and receipts'
        },
        {
            action: 'subscription_management',
            title: 'Subscription',
            description: isCancellationScheduled
                ? 'Review your scheduled cancellation and final access date in Stripe.'
                : hasManagedSubscription
                    ? 'Review or cancel your current subscription in Stripe.'
                    : 'Review any previous subscriptions stored in Stripe.',
            buttonLabel: isCancellationScheduled ? 'Review subscription' : 'Manage subscription'
        }
    ];

    const handleOpenPortal = async (action: CustomerPortalAction) => {
        if (!token || loadingAction) {
            return;
        }

        setPortalErrors((current) => ({ ...current, [action]: undefined }));
        setLoadingAction(action);
        try {
            const response = await createCustomerPortalSession(token, action);
            if (response?.Url) {
                window.location.href = response.Url;
                return;
            }
            setPortalErrors((current) => ({
                ...current,
                [action]: 'Stripe did not return a billing portal link. Please try again.'
            }));
        } catch (error: unknown) {
            setPortalErrors((current) => ({
                ...current,
                [action]: getPaymentErrorMessage(error)
            }));
        } finally {
            setLoadingAction(null);
        }
    };

    return (
        <section className={styles.section} aria-labelledby="billing-actions-title">
            <div className={styles.heading}>
                <h2 className={styles.title} id="billing-actions-title">Manage billing</h2>
                <p className={styles.copy}>Payment details, invoices and subscriptions are securely hosted by Stripe.</p>
            </div>

            <div className={styles.actionList}>
                {billingActions.map(({ action, title, description, buttonLabel }) => (
                    <div className={styles.actionRow} key={action}>
                        <div className={styles.actionCopy}>
                            <h3>{title}</h3>
                            <p>{description}</p>
                            {portalErrors[action] ? (
                                <p className={styles.error} role="alert">{portalErrors[action]}</p>
                            ) : null}
                        </div>
                        <button
                            type="button"
                            className={styles.portalButton}
                            onClick={() => void handleOpenPortal(action)}
                            disabled={loadingAction !== null}
                        >
                            {loadingAction === action ? 'Opening Stripe...' : buttonLabel}
                        </button>
                    </div>
                ))}
            </div>

            <button
                type="button"
                className={styles.portalLink}
                onClick={() => void handleOpenPortal('portal')}
                disabled={loadingAction !== null}
            >
                {loadingAction === 'portal' ? 'Opening Stripe...' : 'Open complete Stripe portal'}
            </button>
            {portalErrors.portal ? <p className={styles.error} role="alert">{portalErrors.portal}</p> : null}
        </section>
    );
};

export default BillingPortalPanel;
